import React from "react";
import { useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { GlobalService } from "../service/GlobalService";

// Retry function with exponential backoff for 429 errors
const retryRequest = async (requestFn, maxRetries = 3, delay = 1000) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await requestFn();
    } catch (error) {
      if (error.response?.status === 429 && i < maxRetries - 1) {
        const waitTime = delay * Math.pow(2, i); // Exponential backoff
        console.log(`429 error, retrying in ${waitTime}ms... (attempt ${i + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
        continue;
      }
      throw error;
    }
  }
};

function CustomerLedger() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const cust_id = queryParams.get("custId");
  const fromDate = queryParams.get("fromDate");
  const toDate = queryParams.get("toDate");
  const [pendingAmt, setPendingAmt] = useState(0);

  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [contactNo, setContactNo] = useState("");
  const [loadingLedger, setLoadingLedger] = useState(false);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [totalDebit, setTotalDebit] = useState(0);
  const [totalCredit, setTotalCredit] = useState(0);
  const [closingBalance, setClosingBalance] = useState(0);

  // Use refs to track if requests are in progress
  const ledgerDetailsAbortRef = useRef(null);

  const [tableData, setTableData] = useState([]);
  useEffect(() => {
    if (!cust_id || !fromDate || !toDate) return;

    // Cancel previous request if it exists
    if (ledgerDetailsAbortRef.current) {
      ledgerDetailsAbortRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    ledgerDetailsAbortRef.current = abortController;

    const getLedgerDetails = async () => {
      if (loadingLedger) return; // Prevent duplicate requests
      
      setLoadingLedger(true);
      try {
        // Create FormData for POST request
        const formData = new FormData();
        formData.append('customer_id', cust_id);
        formData.append('from_date', fromDate);
        formData.append('to_date', toDate);

        const response = await retryRequest(async () => {
          return await axios.post(
            `${GlobalService.path}/customerLedger1`,
            formData,
            { 
              signal: abortController.signal,
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            }
          );
        });

        if (abortController.signal.aborted) return;

        const ledgerData = response.data;
        
        // Set customer details from response
        if (ledgerData.customer) {
          setName(ledgerData.customer.name || "");
          setContactNo(ledgerData.customer.mobile || "");
          // Note: Address is not in the new API response, keeping existing state
        }
        
        // Set ledger data
        if (ledgerData.ledger && Array.isArray(ledgerData.ledger)) {
          setTableData(ledgerData.ledger);
        } else {
          setTableData([]);
        }
        
        // Set summary values
        setOpeningBalance(ledgerData.opening_balance || 0);
        setTotalDebit(ledgerData.total_debit || 0);
        setTotalCredit(ledgerData.total_credit || 0);
        setClosingBalance(ledgerData.closing_balance || 0);
        setPendingAmt(ledgerData.closing_balance || 0);
      } catch (error) {
        if (axios.isCancel(error)) {
          console.log("Request cancelled");
          return;
        }
        if (error.response?.status === 429) {
          console.error("Too many requests. Please wait a moment and refresh the page.");
        } else {
          console.error("Error fetching ledger details:", error);
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoadingLedger(false);
        }
      }
    };

    getLedgerDetails();

    // Cleanup function
    return () => {
      if (ledgerDetailsAbortRef.current) {
        ledgerDetailsAbortRef.current.abort();
      }
    };
  }, [cust_id, fromDate, toDate]);

  // Sort data by date
  const filteredData = [...tableData].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  // Helper: get Indian Financial Year (1 Apr – 31 Mar) from a date
  const getFinancialYear = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return null;

    const month = d.getMonth() + 1; // 1–12
    const year = d.getFullYear();

    // FY is 1 April (4) to 31 March (3)
    if (month >= 4) {
      // April–Dec → FY YYYY-(YYYY+1)
      return `${year}-${String(year + 1).slice(-2)}`;
    } else {
      // Jan–Mar → FY (YYYY-1)-YYYY
      return `${year - 1}-${String(year).slice(-2)}`;
    }
  };

  // Build a list that includes display-only FY totals + Opening/Closing rows in between normal rows.
  // हे फक्त frontend-display साठी आहे. Existing API totals / running balance ला अजिबात स्पर्श होत नाही.
  const ledgerWithFinancialYearRows = (() => {
    if (!filteredData || filteredData.length === 0) return [];

    const result = [];
    let previousFY = getFinancialYear(filteredData[0].date);
    let fyOpening = openingBalance || 0; // त्या FY चे opening (सुरुवातीला API opening)
    let fyDebit = 0;
    let fyCredit = 0;

    filteredData.forEach((row, index) => {
      const currentFY = getFinancialYear(row.date);
      const rowDebit = parseFloat(row.debit || 0);
      const rowCredit = parseFloat(row.credit || 0);

      // If FY changed between previous row and this row, insert FY totals + Closing & Opening rows
      if (index > 0 && previousFY && currentFY && previousFY !== currentFY) {
        // त्या FY साठी closing = Opening + TotalDebit - TotalCredit
        const fyClosingBalance = fyOpening + fyDebit - fyCredit;
        const closingAmt = Math.abs(fyClosingBalance);
        const isDebitClosing = fyClosingBalance >= 0;

        // 1) Totals row: त्या FY मधील Debit / Credit एकूण
        result.push({
          __isFYRow: true,
          __fyType: "total",
          __fyLabel: previousFY,
          __fyDebit: fyDebit,
          __fyCredit: fyCredit,
        });

        // 2) Closing Balance row for previous FY (immediately after last txn of that FY)
        result.push({
          __isFYRow: true,
          __fyType: "closing",
          __fyLabel: `By Closing Balance (${previousFY})`,
          __fyAmount: closingAmt,
          __fyIsDebit: isDebitClosing,
        });

        // 3) Opening Balance row for new FY (immediately before first txn of new FY)
        result.push({
          __isFYRow: true,
          __fyType: "opening",
          __fyLabel: `To Opening Balance (${currentFY})`,
          __fyAmount: closingAmt,
          __fyIsDebit: isDebitClosing,
        });

        // New FY segment starts
        fyOpening = fyClosingBalance;
        fyDebit = 0;
        fyCredit = 0;
      }

      // Push the real transaction row (unchanged data)
      result.push({
        ...row,
        __isFYRow: false,
      });

      // त्या FY साठी running totals (फक्त display साठी)
      fyDebit += rowDebit;
      fyCredit += rowCredit;
      previousFY = currentFY || previousFY;
    });

    return result;
  })();

  return (
    <div>
      <div>
        <style
          type="text/css"
          dangerouslySetInnerHTML={{
            __html:
              "\n   \n      .invoice-box {\n        max-width: 210mm;\n        margin: auto;\n        height: 260mm;\n       \n        border: 2px solid black;\n          position: relative;\n   \n      \n        font-family: 'Helvetica Neue', 'Helvetica', Helvetica, Arial, sans-serif;\n        color: #555;\n      }\n\n      .invoice-header {\n        max-width: 210mm;\n    \n        font-size: 16px;\n        line-height: 24px;\n        font-family: 'Helvetica Neue', 'Helvetica', Helvetica, Arial, sans-serif;\n        color: #555;\n      }\n\ntable {\n  font-family: 'Helvetica Neue', 'Helvetica', Helvetica, Arial, sans-serif;\n  border-collapse: collapse;\n  width: 100%;\n}\n\n\n\n      \n\n      @media only screen and (max-width: 600px) {\n        .invoice-box table tr.top table td {\n          width: 100%;\n          display: block;\n          text-align: center;\n          margin: 5px;\n        }\n\n        .invoice-box table tr.information table td {\n          width: 100%;\n          display: block;\n          text-align: center;\n\n        }\n        .tables tfoot\n      {\n         display: table-footer-group;\n      }\n      }\n\n      /* RTL */\n      .invoice-box.rtl {\n        direction: rtl;\n        font-family: Tahoma, 'Helvetica Neue', 'Helvetica', Helvetica, Arial, sans-serif;\n      }\n\n           .tables {\n        font-family: 'Helvetica Neue', 'Helvetica', Helvetica, Arial, sans-serif;\n        height: 480px;\n\n        width: 100%;\n\n      }\n     \n\n      .tables tfoot\n      {\n        bottom: 0;\n        \n      }\n\n      td, th {\n        border: 1px solid black;\n\n        text-align: left;\n        padding: 2px;\n      }\n\n\n    ",
          }}
        />
        <div
          className="invoice-box "
          style={{ height: "auto", minHeight: "700px",padding:"12px" }}
        >
          <div
            className="invoice-header"
            style={{
              display: "flex",
              marginTop: "12px",
              width: "50%",
              marginLeft: "auto",
              marginRight: "auto",
              justifyContent:"center",
          alignItems:"center"
            }}
          >
            <div style={{ flexDirection: "column", textAlign: "center" }}>
              <h4>
                <b>GLOWLINE THERMOPLASTIC PAINTS</b>
              </h4>
              <p style={{ fontSize: "14px" }}>KOLHAPUR,MAHARASHTRA</p>
              <p style={{ fontSize: "14px" }}>shubhamkurade63@gmail.com</p>
              <p style={{ fontSize: "14px" }}>9604096305</p>
              <h4>
                <b>{name}</b>
              </h4>
              <p style={{ fontSize: "14px" }}>{address}</p>

              <p style={{ fontSize: "14px" }}>
                {fromDate} to {toDate}
              </p>
            </div>
          </div>
          <br />
          <table className="tables">
            <thead>
              <tr>
                <th style={{ borderRight: "0", borderLeft: "0", color: "black"}}>Date</th>
                <th style={{ fontWeight: "bold", borderRight: "0", borderLeft: "0", color: "black" }}>Particulars</th>
                <th style={{ borderRight: "0", borderLeft: "0", color: "black"}}>Vch Type</th>
                <th style={{ borderRight: "0", borderLeft: "0", color: "black"}}>Vch No.</th>
                <th style={{ fontWeight: "bold", borderRight: "0", borderLeft: "0", color: "black"}}>Debit</th>
                <th style={{ fontWeight: "bold", borderRight: "0", borderLeft: "0", color: "black" }}>Credit</th>
              </tr>
            </thead>
            <tbody>
              {openingBalance !== 0 && (
                <tr style={{ paddingTop: "10px", paddingBottom: "10px" }}>
                  <td style={{ border: "0", paddingTop: "10px", paddingBottom: "10px", color: "black" }}></td>
                  <td style={{ fontWeight: "bold", border: "0px", textDecoration: "underline", paddingTop: "10px", paddingBottom: "10px", color: "black" }}>
                    Opening Balance
                  </td>
                  <td style={{ border: "0", paddingTop: "10px", paddingBottom: "10px", color: "black" }}></td>
                  <td style={{ border: "0", paddingTop: "10px", paddingBottom: "10px", color: "black" }}></td>
                  <td style={{ border: "0", fontWeight: "bold", paddingTop: "10px", paddingBottom: "10px", color: "black" }}>
                    {openingBalance > 0 ? `₹${parseFloat(openingBalance).toLocaleString('en-IN')}` : ""}
                  </td>
                  <td style={{ border: "0", fontWeight: "bold", paddingTop: "10px", paddingBottom: "10px", color: "black" }}>
                    {openingBalance < 0 ? `₹${parseFloat(Math.abs(openingBalance)).toLocaleString('en-IN')}` : ""}
                  </td>
                </tr>
              )}
              {ledgerWithFinancialYearRows.length > 0 &&
                ledgerWithFinancialYearRows.map((row, index) => {
                  // Extra Financial Year rows (display-only)
                  if (row.__isFYRow) {
                    // 1) Totals row: Debit / Credit sums for that FY
                    if (row.__fyType === "total") {
                      const totalDebit =
                        row.__fyDebit && !isNaN(row.__fyDebit)
                          ? `₹${row.__fyDebit.toLocaleString('en-IN')}`
                          : "0";
                      const totalCredit =
                        row.__fyCredit && !isNaN(row.__fyCredit)
                          ? `₹${row.__fyCredit.toLocaleString('en-IN')}`
                          : "0";

                      return (
                        <React.Fragment key={`fy-total-${row.__fyLabel}-${index}`}>
                          {/* वरची line (फक्त Debit/Credit कॉलम वर) */}
                          <tr>
                            <td style={{ border: "0" }}></td>
                            <td style={{ border: "0" }}></td>
                            <td style={{ border: "0" }}></td>
                            <td style={{ border: "0" }}></td>
                            <td
                              colSpan="2"
                              style={{
                                border: "0",
                                borderTop: "1px solid black",
                                padding: "0",
                                height: "0",
                              }}
                            ></td>
                          </tr>
                          {/* त्या FY साठी Debit / Credit totals */}
                          <tr>
                            <td style={{ border: "0" }}></td>
                            <td style={{ border: "0" }}></td>
                            <td style={{ border: "0" }}></td>
                            <td style={{ border: "0" }}></td>
                            <td
                              style={{
                                border: "0",
                                fontWeight: "bold",
                                color: "black",
                              }}
                            >
                              {totalDebit}
                            </td>
                            <td
                              style={{
                                border: "0",
                                fontWeight: "bold",
                                color: "black",
                              }}
                            >
                              {totalCredit}
                            </td>
                          </tr>
                        </React.Fragment>
                      );
                    }

                    // 2) Closing / Opening rows: show closing amount on Dr/Cr side based on sign
                    const amt =
                      row.__fyAmount && !isNaN(row.__fyAmount)
                        ? `₹${row.__fyAmount.toLocaleString('en-IN')}`
                        : "";
                    const showInDebit = row.__fyIsDebit !== false; // default -> debit

                    return (
                      <React.Fragment key={`fy-${row.__fyType}-${index}`}>
                        <tr style={{ paddingTop: "10px", paddingBottom: "10px" }}>
                          <td style={{ border: "0", paddingTop: "10px", paddingBottom: "10px", color: "black" }}></td>
                          <td
                            style={{
                              fontWeight: "bold",
                              border: "0px",
                              textDecoration: "underline",
                              paddingTop: "10px",
                              paddingBottom: "10px",
                              color: "black",
                            }}
                          >
                            {row.__fyLabel}
                          </td>
                          <td style={{ border: "0", paddingTop: "10px", paddingBottom: "10px", color: "black" }}></td>
                          <td style={{ border: "0", paddingTop: "10px", paddingBottom: "10px", color: "black" }}></td>
                          {/* Display-only amount: Dr किंवा Cr पैकी एकाच बाजूला */}
                          <td
                            style={{
                              border: "0",
                              fontWeight: "bold",
                              paddingTop: "10px",
                              paddingBottom: "10px",
                              color: "black",
                            }}
                          >
                            {showInDebit ? amt : ""}
                          </td>
                          <td
                            style={{
                              border: "0",
                              fontWeight: "bold",
                              paddingTop: "10px",
                              paddingBottom: "10px",
                              color: "black",
                            }}
                          >
                            {!showInDebit ? amt : ""}
                          </td>
                        </tr>
                        {/* Closing/Opening नंतरची line (Debit/Credit वर) */}
                        {row.__fyType === "closing" && (
                          <tr>
                            <td style={{ border: "0" }}></td>
                            <td style={{ border: "0" }}></td>
                            <td style={{ border: "0" }}></td>
                            <td style={{ border: "0" }}></td>
                            <td
                              colSpan="2"
                              style={{
                                border: "0",
                                borderTop: "1px solid black",
                                padding: "0",
                                height: "0",
                              }}
                            ></td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  }

                  // Normal transaction row – existing JSX and logic preserved
                  return (
                    <tr key={index}>
                      <td style={{ border: "0", color: "black" }}>{row.date}</td>
                      <td style={{ fontWeight: "bold", border: "0px", color: "black" }}>
                        {row.particulars}
                      </td>
                      <td style={{ fontWeight: "bold", border: "0px", color: "black" }}>
                        {row.vch_type}
                      </td>
                      <td style={{ border: "0", color: "black" }}>{row.vch_no}</td>
                      <td style={{ border: "0", color: "black" }}>
                        {row.debit ? parseFloat(row.debit).toLocaleString('en-IN') : 0}
                      </td>
                      <td style={{ border: "0", color: "black" }}>
                        {row.credit ? parseFloat(row.credit).toLocaleString('en-IN') : 0}
                      </td>
                    </tr>
                  );
                })}
              <tr style={{ height: "100%" }}>
                <td style={{ border: "0" }}></td>
                <td style={{ border: "0" }}></td>
                <td style={{ border: "0" }}></td>
                <td style={{ border: "0" }}></td>
                <td style={{ border: "0" }}></td>
                <td style={{ border: "0" }}></td>
              </tr>
              <tr>
              <td colSpan="4" style={{ border: "0" }}></td>
              <td colSpan="2" style={{ border: "0", borderTop: "1px solid black", padding: "0", height: "0" }}></td>
            </tr>
            <tr>
              <th style={{ border: "0" }} />
              <th style={{ border: "0" }} />
              <th style={{ border: "0" }} />
              <th style={{ border: "0" }} />

              <td
                style={{
                  borderLeft: "0",
                  borderRight: "0",
                  borderBottom: "0",
                  borderTop: "0",
                  fontWeight: "bold",
                  color: "black"
                }}
              >
                &#8377;{totalDebit ? parseFloat(totalDebit).toLocaleString('en-IN') : 0}
              </td>
              <td
                style={{
                  borderRight: "0",
                  borderLeft: "0",
                  borderBottom: "0",
                  borderTop: "0",
                  fontWeight: "bold",
                  color: "black"
                }}
              >
                &#8377;{totalCredit ? parseFloat(totalCredit).toLocaleString('en-IN') : 0}
              </td>
            </tr>
            <tr style={{ paddingTop: "10px", paddingBottom: "10px" }}>
              <td style={{ border: "0", paddingTop: "10px", paddingBottom: "10px", color: "black" }}></td>
              <td style={{ fontWeight: "bold", border: "0px", textDecoration: "underline", paddingTop: "10px", paddingBottom: "10px", color: "black" }}>
                Closing Balance
              </td>
              <td style={{ border: "0", paddingTop: "10px", paddingBottom: "10px", color: "black" }}></td>
              <td style={{ border: "0", paddingTop: "10px", paddingBottom: "10px", color: "black" }}></td>
              <td style={{ border: "0", borderTop: "0", borderLeft: "0", borderRight: "0", borderBottom: "0", fontWeight: "bold", paddingTop: "10px", paddingBottom: "10px", color: "black" }}>
                {closingBalance > 0 ? `₹${parseFloat(closingBalance).toLocaleString('en-IN')}` : ""}
              </td>
              <td style={{ border: "0", borderTop: "0", borderRight: "0", borderLeft: "0", borderBottom: "0", fontWeight: "bold", paddingTop: "10px", paddingBottom: "10px", color: "black" }}>
                {closingBalance < 0 ? `₹${parseFloat(Math.abs(closingBalance)).toLocaleString('en-IN')}` : ""}
              </td>
            </tr>
            <tr>
              <td colSpan="4" style={{ border: "0" }}></td>
              <td colSpan="2" style={{ border: "0", borderTop: "1px solid black", padding: "0", height: "0" }}></td>
            </tr>
            
            </tbody>
            
              
            
          </table>
        </div>
      </div>
    </div>
  );
}

export default CustomerLedger;
